import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Coin } from '@/components/ui';
import { BookingTypeIcon } from '@/components/companion/ServiceIcon';
import { bookingTypeLabel, unitLabel, type Rate, type ServiceListing } from '@/lib/data/services';
import { createBooking, useAvailability, collides, formatDuration } from '@/lib/data/bookings';

/**
 * Confirm a booking.
 *
 * Two shapes behind one dialog, because §55 makes them one flow:
 *
 *   online     — chat, voice, video. Starts as soon as they accept, so there
 *                is nothing to schedule. One reserved minute proves the wallet
 *                is funded; metering settles the rest once live sessions land.
 *   in person  — needs a date, a length and a place. §20.
 */
const DURATIONS = [60, 90, 120, 180, 240, 360] as const;

export default function BookingDialog({
  service,
  rate,
  balance,
  onBooked,
  onClose,
}: {
  service: ServiceListing;
  rate: Rate;
  balance: number;
  onBooked: () => void;
  onClose: () => void;
}) {
  const scheduled = rate.bookingType === 'in_person';
  const { taken } = useAvailability(scheduled ? service.companionId : undefined);

  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [minutes, setMinutes] = useState<number>(60);
  const [location, setLocation] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const startIso = useMemo(() => {
    if (!date || !time) return null;
    const d = new Date(`${date}T${time}`);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }, [date, time]);

  // Per-hour rounds up to the whole coin, matching booking_cost_coins in the
  // database. Showing a different number here than the one charged would be
  // the worst kind of small bug.
  const cost = scheduled
    ? Math.ceil((rate.priceCoins * minutes) / 60)
    : rate.priceCoins;

  const inPast = startIso !== null && new Date(startIso).getTime() <= Date.now();
  const clash = startIso !== null && collides(startIso, minutes, taken);
  const affordable = balance >= cost;

  const blocker = !affordable
    ? `You need ${cost} coins and have ${balance}.`
    : scheduled && !startIso
      ? 'Choose a date and time.'
      : inPast
        ? 'That time has already passed.'
        : clash
          ? 'They already have a booking then. Try another time.'
          : null;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await createBooking({
        serviceId: service.id,
        bookingType: rate.bookingType,
        scheduledStart: scheduled && startIso ? startIso : undefined,
        durationMinutes: scheduled ? minutes : undefined,
        location: scheduled ? location.trim() || undefined : undefined,
        note: note.trim() || undefined,
      });
      onBooked();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send that request.');
      setBusy(false);
    }
  }

  // Today, in the browser's timezone — not UTC, which would let someone in IST
  // pick a date that has already begun.
  const today = new Date();
  const minDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
    today.getDate(),
  ).padStart(2, '0')}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4"
      style={{ background: 'rgb(0 0 0 / 0.5)' }}
      onClick={() => !busy && onClose()}
      role="presentation"
    >
      <Card
        raised
        className="animate-rise my-auto w-full max-w-md p-6"
        // Stop a click inside the card from closing it.
        style={{ cursor: 'default' }}
      >
        <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
          <div className="flex items-start gap-3">
            <span
              className="grid size-10 shrink-0 place-items-center rounded-xl"
              style={{ background: 'var(--surface-2)', color: 'var(--marigold)' }}
            >
              <BookingTypeIcon type={rate.bookingType} size={20} />
            </span>
            <div>
              <h2 className="text-lg font-semibold">{service.categoryName}</h2>
              <p className="text-sm text-[var(--ink-2)]">
                {bookingTypeLabel(rate.bookingType)} with {service.displayName}
              </p>
            </div>
          </div>

          {scheduled ? (
            <div className="mt-5 flex flex-col gap-4">
              <div className="flex gap-3">
                <label className="flex-1 text-sm">
                  <span className="mb-1.5 block font-medium">Date</span>
                  <input
                    type="date"
                    value={date}
                    min={minDate}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full rounded-lg px-3 py-2 text-sm outline-none"
                    style={{
                      background: 'var(--surface)',
                      color: 'var(--ink)',
                      border: '1px solid var(--line)',
                    }}
                  />
                </label>
                <label className="flex-1 text-sm">
                  <span className="mb-1.5 block font-medium">Time</span>
                  <input
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    className="w-full rounded-lg px-3 py-2 text-sm outline-none"
                    style={{
                      background: 'var(--surface)',
                      color: 'var(--ink)',
                      border: '1px solid var(--line)',
                    }}
                  />
                </label>
              </div>

              <div>
                <span className="mb-1.5 block text-sm font-medium">How long</span>
                <div className="flex flex-wrap gap-2">
                  {DURATIONS.map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setMinutes(m)}
                      className="rounded-full px-3 py-1.5 text-sm transition-all duration-150 hover:-translate-y-px"
                      style={
                        minutes === m
                          ? { background: 'var(--marigold)', color: '#fff' }
                          : {
                              background: 'var(--surface)',
                              color: 'var(--ink-2)',
                              border: '1px solid var(--line)',
                            }
                      }
                    >
                      {formatDuration(m)}
                    </button>
                  ))}
                </div>
              </div>

              <label className="text-sm">
                <span className="mb-1.5 block font-medium">Where</span>
                <input
                  value={location}
                  maxLength={300}
                  placeholder="Café name, mall, area"
                  onChange={(e) => setLocation(e.target.value)}
                  className="w-full rounded-lg px-3 py-2 text-sm outline-none"
                  style={{
                    background: 'var(--surface)',
                    color: 'var(--ink)',
                    border: '1px solid var(--line)',
                  }}
                />
                <span className="mt-1 block text-xs text-[var(--muted)]">
                  A public place for a first meeting. Shared only after they accept.
                </span>
              </label>
            </div>
          ) : (
            <p className="mt-5 text-sm leading-relaxed text-[var(--ink-2)]">
              They have {/* expiry is set from platform settings */}a short window to accept. Once
              they do, {rate.priceCoins} {rate.priceCoins === 1 ? 'coin' : 'coins'} per minute is
              charged for the time you actually spend — you can end it whenever you like.
            </p>
          )}

          <label className="mt-4 block text-sm">
            <span className="mb-1.5 block font-medium">Anything they should know?</span>
            <textarea
              rows={2}
              value={note}
              maxLength={500}
              placeholder="Optional"
              onChange={(e) => setNote(e.target.value)}
              className="w-full rounded-lg px-3 py-2 text-sm outline-none"
              style={{
                background: 'var(--surface)',
                color: 'var(--ink)',
                border: '1px solid var(--line)',
              }}
            />
          </label>

          {/* Cost */}
          <div
            className="mt-5 flex items-center justify-between rounded-xl px-4 py-3"
            style={{ background: 'var(--surface-2)' }}
          >
            <div>
              <p className="text-sm text-[var(--ink-2)]">
                {scheduled ? formatDuration(minutes) : 'Per minute'} ·{' '}
                {rate.priceCoins}
                {unitLabel(rate.unit)}
              </p>
              <p className="mt-0.5 text-xs text-[var(--muted)]">
                Held when they accept, not now.
              </p>
            </div>
            <span className="flex items-center gap-1.5 text-lg font-semibold tabular-nums">
              <Coin className="text-[var(--marigold)]" />
              {cost.toLocaleString('en-IN')}
            </span>
          </div>

          {(blocker || error) && (
            <p
              className="mt-3 text-sm"
              style={{ color: error ? 'var(--danger)' : 'var(--ink-2)' }}
            >
              {error ?? blocker}
            </p>
          )}

          <div className="mt-5 flex gap-3">
            <Button onClick={() => void submit()} disabled={busy || blocker !== null} full>
              {busy ? 'Sending…' : 'Send request'}
            </Button>
            <Button variant="secondary" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
          </div>

          {!affordable && (
            <div className="mt-3">
              <Button to="/wallet" variant="ghost" size="sm" full>
                Add coins
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
